/***************************************************************************/
"use client";
/***************************************************************************/

import { useFetch } from "@/client/api/use-fetch";
import { TError, TResponseBody, TValidationError } from "@/universal/api/types";
import useSWR, { SWRConfiguration } from "swr";
import { TFetchResponse, TQueryConfig } from "./types";

/**
 * Represents the state returned by the `useQuery` hook.
 *
 * @template TResponseData - The type of the expected response data.
 * @template TValidationErrorField - The type of validation error fields.
 */
export type TState<
  TResponseData,
  TValidationErrorField extends string = string,
> = {
  /** Indicates whether the request is loading. */
  loading: boolean;
  /** Indicates whether the request is currently being revalidated. */
  validating: boolean;
  /** The response data from the request, if available. */
  data: TResponseData | undefined;
  /** The error response, if an error occurred. */
  error: TError | undefined;
  /** The validation errors associated with the request, if any. */
  validationErrors: TValidationError<TValidationErrorField> | undefined;
  /** The status of the request, either "success" or "error". */
  status: "success" | "error" | undefined;
  /** The HTTP status code of the response. */
  statusCode: number | undefined;
  /** The HTTP status text of the response. */
  statusText: string | undefined;
};

/**
 * A custom React hook for fetching data with caching and revalidation using SWR.
 *
 * @template TResponseData - The type of the expected response data.
 * @template TValidationErrorField - The type of validation error fields.
 * @param {URL | string} url - The URL for the API request.
 * @param {SWRConfiguration} [swrConfig] - Optional SWR configuration settings.
 * @param {RequestInit} [fetchOptions] - Optional fetch options for customization.
 * @param {TQueryConfig} [fetchConfig] - Additional query configuration, including a caching key.
 * @returns {TState<TResponseData, TValidationErrorField>} The state of the request, including loading, data, and errors.
 */
const useQuery = <TResponseData, TValidationErrorField extends string = string>(
  url: URL | string,
  swrConfig?: SWRConfiguration<
    TFetchResponse<TResponseBody<TResponseData> | undefined>,
    Error
  >,
  fetchOptions?: RequestInit,
  fetchConfig?: TQueryConfig,
): TState<TResponseData, TValidationErrorField> => {
  const fetcher = useFetch();

  const {
    isLoading,
    isValidating,
    data: response,
  } = useSWR<TFetchResponse<TResponseBody<TResponseData> | undefined>, Error>(
    fetchConfig?.key ?? url.toString(),
    () =>
      fetcher.get<TResponseBody<TResponseData>>(url, fetchOptions, fetchConfig),
    swrConfig,
  );

  return {
    loading: isLoading,
    validating: isValidating,
    data: response?.data?.data,
    error: response?.data?.error,
    validationErrors: undefined,
    status:
      response?.ok === undefined
        ? undefined
        : response.ok
          ? "success"
          : "error",
    statusCode: response?.status,
    statusText: response?.statusText,
  };
};

export { useQuery };
